/**
 * Task #4257 — helper di presentazione delle concessioni (condivisi tra
 * editor utente e ActiveGrantsTab).
 */
import { GRANT_SCOPE_LABELS } from './permission-labels';
import type { PermissionGrant, PermissionGrantKind, PermissionGrantRevokeReason } from '@easyflow/shared';

export function formatGrantDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatGrantDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Portata leggibile: livello + dettaglio (RS o sedi). */
export function describeGrantScope(grant: PermissionGrant): string {
  const label = GRANT_SCOPE_LABELS[grant.scope_level];
  if (grant.scope_level === 'company') return `${label}: ${grant.company_name ?? grant.company_id ?? '—'}`;
  if (grant.scope_level === 'stores') {
    const names = grant.store_names.length > 0 ? grant.store_names : grant.store_ids;
    const shown = names.slice(0, 3).join(', ');
    return `${label}: ${names.length > 3 ? `${shown} +${names.length - 3}` : shown}`;
  }
  return label;
}

export const GRANT_STATUS_LABELS: Record<PermissionGrant['status'], string> = {
  active: 'Attiva',
  expired: 'Scaduta',
  revoked: 'Revocata',
};

/** Giorni mancanti alla scadenza (null = indeterminata o non attiva). */
export function daysToExpiry(grant: PermissionGrant, now: number = Date.now()): number | null {
  if (grant.status !== 'active' || !grant.valid_until) return null;
  const t = new Date(grant.valid_until).getTime();
  return Math.max(0, Math.ceil((t - now) / 86_400_000));
}

/** Etichetta breve del tipo per la colonna "Tipo" della lista tenant-wide. */
export const GRANT_KIND_SHORT_LABELS: Record<PermissionGrantKind, string> = {
  potere: 'Potere',
  allargamento: 'Recinto',
};

/** Motivo della chiusura (null = revoca manuale legacy, senza motivo tracciato). */
export const GRANT_REVOKE_REASON_LABELS: Record<PermissionGrantRevokeReason, string> = {
  manual: 'Revocata',
  replaced: 'Sostituita',
};

/** Stato leggibile che distingue la revoca manuale dalla sostituzione tracciata. */
export function describeGrantStatus(grant: PermissionGrant): string {
  if (grant.status === 'revoked' && grant.revoke_reason) return GRANT_REVOKE_REASON_LABELS[grant.revoke_reason];
  return GRANT_STATUS_LABELS[grant.status];
}

/** Valore per `<input type="date">` (data LOCALE) a partire da un timestamp; '' se assente. */
export function toDateInputValue(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Fine giornata locale di una data `yyyy-mm-dd` in ISO; null se vuota (stessa semantica degli editor della scheda utente). */
export function dateInputToValidUntil(value: string): string | null {
  return value ? new Date(`${value}T23:59:59`).toISOString() : null;
}

/** Oggi in formato `yyyy-mm-dd` locale (min per gli input di scadenza). */
export function todayDateInputValue(): string {
  return toDateInputValue(new Date());
}
